import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/utils/core";

const iconButtonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1918] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:text-disabled",
  {
    variants: {
      variant: {
        neutral:
          "text-secondary hover:bg-subtle hover:text-primary active:bg-background disabled:bg-transparent",
        destructive: "text-error hover:bg-red-50 active:bg-red-100 disabled:bg-transparent",
        inverse:
          "text-white/80 hover:bg-white/10 hover:text-white active:bg-white/20 disabled:bg-transparent",
      },
      size: {
        sm: "size-8 [&_svg]:size-4",
        md: "size-9 [&_svg]:size-[18px]",
        lg: "size-10 [&_svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "sm",
    },
  },
);

export interface IconButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-label">,
    VariantProps<typeof iconButtonVariants> {
  label: string;
  tooltip?: boolean;
  asChild?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, label, tooltip = true, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const button = (
      <Comp
        ref={ref}
        type={asChild ? undefined : "button"}
        aria-label={label}
        className={cn(iconButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    return tooltip && !props.disabled ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      button
    );
  },
);
IconButton.displayName = "IconButton";

export { iconButtonVariants };
